import type { Notification } from 'types';
import { useNavigate } from 'react-router';
import { useAuth } from 'hooks/useAuth';
import { cn } from 'helpers/cn';
import { withAgentNote } from 'helpers/notification-text';
import { describe } from './descriptors';

type NotificationRowProps = {
  notification: Notification;
  onRead: (id: number) => void;
  onNavigated: () => void;
};

const relativeTime = (timestamp: number): string => {
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
};

export const NotificationRow = ({ notification, onRead, onNavigated }: NotificationRowProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const descriptor = describe(notification);
  const Icon = descriptor.icon;
  const isUnread = !notification.readAt;
  // An agent's action is marked, as "your agent" for the person it acted for (PER-348).
  const body = withAgentNote({
    body: descriptor.body?.(notification.payload) ?? '',
    type: notification.type,
    payload: notification.payload,
    readerIsOwner: user?.id != null && user.id === notification.actorUserId,
  });
  const target = descriptor.link(notification);

  const handleClick = () => {
    if (isUnread) onRead(notification.id);
    if (target) {
      navigate(target);
      onNavigated();
    }
  };

  return (
    <button
      onClick={handleClick}
      className={cn(
        // The hairline is the divider between rows; the 2px left edge is what marks a
        // row as unread, together with the panel2 fill behind it.
        'border-line hover:bg-hover flex w-full items-start gap-3 border-b border-l-2 px-4 py-3 text-left',
        'transition-colors duration-150',
        isUnread ? 'border-l-accent bg-panel2' : 'border-l-transparent',
      )}
    >
      <Icon className={cn('mt-0.5 size-3.5 shrink-0', descriptor.tone)} />

      <div className="min-w-0 flex-1">
        <p className={cn('text-foreground text-body leading-snug', isUnread && 'font-medium')}>
          {descriptor.title(notification.payload)}
        </p>
        {!!body && <p className="supporting truncate">{body}</p>}
        <p className="log-line text-dim mt-0.5">{relativeTime(notification.createdAt)}</p>
      </div>

      {isUnread && <span className="bg-accent rounded-pill mt-1.5 size-1.5 shrink-0" aria-label="Unread" />}
    </button>
  );
};
